import clsx from "clsx";
import { Gift, Settings, UserRoundPlus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { NavLink as RouterNavLink, useParams } from "react-router-dom";
import { BalanceIndicator } from "./BalanceIndicator";
import { Button } from "@/components/Button/Button";
import { IconButton } from "@/components/Button/IconButton";
import { TenantImage } from "@/components/Images/TenantImage";
import { AccountSettingsButton } from "@/components/Navigation/AccountSettingsButton";
import { AdminNavigation } from "@/components/Navigation/AdminNavigation";
import { BurgerMenu } from "@/components/Navigation/BurgerMenu";
import { CreateMenu } from "@/components/Navigation/CreateMenu";
import { MobileMenu } from "@/components/Navigation/MobileMenu";
import { NavigationPill } from "@/components/Navigation/NavigationPill";
import SavingIndicator from "@/components/Navigation/SavingIndicator";
import { ResumeName } from "@/components/ResumeName/ResumeName";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useTransaction } from "@/hooks/useTransaction";
import { getShowBonusNavbarButton } from "@/store/app/selectors";
import { setShowBonusNavbarButton } from "@/store/app/slice";
import { openModal } from "@/store/modal/slice";
import { getActiveResume } from "@/store/resume/selectors";
import {
    getIsCreditsEnabled,
    getIsJobsEnabled,
    getIsUserJobsEnabled,
} from "@/store/tenant/selectors";
import { getIsAdminUser } from "@/store/user/selectors";
import { CreditActionCode } from "@/types/creditAction";
import { ModalTypes } from "@/types/modal";
import { AppRoutes } from "@/types/routes";
import {
    getIsDashboardPage,
    getIsExamplesPage,
    getIsJobsPage,
    getIsPreviewPage,
    getIsResumePage,
    getIsYourJobsPage,
} from "@/utils/routes";

export const Navigation = () => {
    const { id } = useParams();
    const isAdminPage = window.location.pathname.includes("admin");
    const isAdmin = useAppSelector(getIsAdminUser);
    const isPreviewPage = getIsPreviewPage();
    const isResumePage = getIsResumePage();
    const activeResume = useAppSelector(getActiveResume);
    const showBonusNavbarButton = useAppSelector(getShowBonusNavbarButton);
    const userCanRefer = false; // useAppSelector(getUserCanRefer); // disabling button for now
    const { getCreditActionByCode } = useTransaction();
    const referAction = useMemo(
        () => getCreditActionByCode(CreditActionCode.USER_REFERRAL),
        [getCreditActionByCode],
    );
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const dispatch = useAppDispatch();
    const isExamplesPage = getIsExamplesPage();
    const isDashboard = getIsDashboardPage();
    const isYourJobsPage = getIsYourJobsPage();
    const isJobsPage = getIsJobsPage();
    const isCreditsEnabled = useAppSelector(getIsCreditsEnabled);
    const isJobsEnabled = useAppSelector(getIsJobsEnabled);
    const isUserJobsEnabled = useAppSelector(getIsUserJobsEnabled);

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(prev => !prev);
    }, []);
    const openCreditsWelcomeModal = useCallback(() => {
        dispatch(openModal({ modalType: ModalTypes.CREDITS_WELCOME }));
        dispatch(setShowBonusNavbarButton(false));
    }, [dispatch]);
    const openReferralModal = useCallback(() => {
        dispatch(openModal({ modalType: ModalTypes.REFERRAL }));
    }, [dispatch]);

    return (
        <>
            <div
                className={clsx(
                    "fixed top-0 z-20  border-b-[1px] border-neutral-300 bg-neutral-50",
                    isPreviewPage ? "w-auto lg:w-full" : "w-full",
                )}
            >
                <div className="bg-neutral-white mb-0 rounded-xl px-3">
                    <div className="relative flex h-navigationHeight items-center justify-between">
                        {/* Desktop Navigation Header */}
                        <div className="hidden items-center gap-2 lg:flex xl:gap-3">
                            <RouterNavLink
                                to={AppRoutes.Dashboard}
                                aria-label="Home"
                            >
                                {isResumePage ? (
                                    <TenantImage
                                        imageType="mobileLogo"
                                        className="min-h-9 w-auto sm:h-4"
                                    />
                                ) : (
                                    <TenantImage
                                        imageType="primaryLogo"
                                        className="h-auto w-[150px]"
                                    />
                                )}
                            </RouterNavLink>
                            {isResumePage && <CreateMenu />}
                            <NavigationPill
                                to={AppRoutes.Dashboard}
                                label="Dashboard"
                                variant={isDashboard ? "light" : "white"}
                            />
                            {isUserJobsEnabled && (
                                <NavigationPill
                                    to={`/${AppRoutes.YourJobs}`}
                                    label="Your Jobs"
                                    variant={isYourJobsPage ? "light" : "white"}
                                />
                            )}
                            {isJobsEnabled && (
                                <NavigationPill
                                    to={`/${AppRoutes.Jobs}`}
                                    label="Job Board"
                                    variant={isJobsPage ? "light" : "white"}
                                />
                            )}
                            <NavigationPill
                                to={`/${AppRoutes.Examples}`}
                                label="Resume Examples"
                                variant={isExamplesPage ? "light" : "white"}
                            />
                            {isResumePage && activeResume && (
                                <ResumeName
                                    resumeId={activeResume.id}
                                    resumeName={activeResume.name}
                                    isBuilderPage={true}
                                />
                            )}
                        </div>
                        <div className="hidden flex-row items-center gap-2 lg:flex xl:gap-3">
                            {isResumePage && activeResume && (
                                <SavingIndicator />
                            )}
                            {showBonusNavbarButton && (
                                <button
                                    onClick={openCreditsWelcomeModal}
                                    className="group relative rounded-full p-2 transition-all duration-300 hover:scale-110"
                                >
                                    <span className="absolute inset-0 animate-ping rounded-full bg-primary-200/20" />
                                    <span className="absolute inset-0 rounded-full bg-primary-200/20" />
                                    <Gift className="relative h-6 w-6 text-primary-700 transition-transform duration-300 group-hover:rotate-12" />
                                </button>
                            )}
                            {userCanRefer && referAction?.isActive && (
                                <Button
                                    variant="solid"
                                    color="secondary"
                                    onClick={openReferralModal}
                                    leftIcon={
                                        <UserRoundPlus className="h-4 w-4" />
                                    }
                                >
                                    <AppText
                                        variant="labelsbuttons"
                                        applyGradient
                                    >
                                        Earn Credits
                                    </AppText>
                                </Button>
                            )}
                            {isCreditsEnabled && <BalanceIndicator />}
                            <AccountSettingsButton />
                            {isAdmin && (
                                <IconButton
                                    variant={"solid"}
                                    color={"red"}
                                    to={`/${AppRoutes.Portal}/${AppRoutes.PortalDashboard}`}
                                >
                                    <Settings className="h-5 w-5" />
                                </IconButton>
                            )}
                        </div>
                        {/* Mobile Navigation Header */}
                        <div
                            className="flex w-full items-center justify-between gap-2 lg:hidden"
                            style={{
                                width: isPreviewPage
                                    ? "calc(100vw - 24px)"
                                    : "",
                            }}
                        >
                            <RouterNavLink
                                to={AppRoutes.Dashboard}
                                aria-label="Home"
                                className="hidden xs:block"
                            >
                                <TenantImage
                                    imageType="primaryLogo"
                                    className="h-5 w-auto sm:h-4"
                                />
                            </RouterNavLink>
                            <RouterNavLink
                                to={AppRoutes.Dashboard}
                                aria-label="Home"
                                className="block xs:hidden"
                            >
                                <TenantImage
                                    imageType="mobileLogo"
                                    className="h-8 w-auto sm:h-4"
                                />
                            </RouterNavLink>
                            <div className="flex items-center gap-2">
                                <BalanceIndicator />
                                <BurgerMenu
                                    isOpen={isMobileMenuOpen}
                                    toggle={toggleMobileMenu}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {isAdminPage && <AdminNavigation />}
            </div>
            <MobileMenu
                isOpen={isMobileMenuOpen}
                onClose={toggleMobileMenu}
            />
            {/* Spacer for navigation */}
            <div
                className={clsx(
                    "navigation-spacer bg-neutral-100",
                    (!id || isAdminPage || isPreviewPage) &&
                        "h-navigationHeight",
                )}
            />
        </>
    );
};
